<template>
  <div v-if="subthread" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md">
    <!-- Header -->
    <div
      class="px-6 py-4 cursor-pointer transition-all duration-200 hover:bg-gray-50"
      @click="toggleExpanded"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center flex-1 gap-6">
          <!-- Color indicator -->
          <div
            class="w-2 h-12 rounded-full shrink-0 shadow-sm"
            :style="{ backgroundColor: subthread.performance_domain_color || '#9CA3AF' }"
          ></div>

          <!-- Thread info -->
          <div class="grid grid-cols-12 gap-4 items-center flex-1">
            <div class="col-span-5">
              <h4 class="font-semibold text-gray-900">{{ subthread.thread_type_name }}</h4>
            </div>
            <div class="col-span-3">
              <div class="flex items-center text-sm text-gray-600">
                <font-awesome-icon :icon="['fas', 'clock']" class="mr-2 text-gray-400" />
                <span class="font-medium">{{ parseFloat(subthread.estimated_time_spent).toFixed(1) }} min</span>
              </div>
            </div>
            <div class="col-span-3">
              <div class="flex items-center text-sm text-gray-600">
                <font-awesome-icon :icon="['fas', 'chart-pie']" class="mr-2 text-gray-400" />
                <span class="font-medium">{{ parseFloat(subthread.thread_percent_of_engagement).toFixed(1) }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Expand icon -->
        <div class="ml-4 transform transition-transform duration-200" :class="{ 'rotate-180': expanded }">
          <font-awesome-icon :icon="['fas', 'chevron-down']" class="text-gray-400" />
        </div>
      </div>
    </div>

    <!-- Expanded Content -->
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="max-h-0 opacity-0"
      enter-to-class="max-h-[2000px] opacity-100"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="max-h-[2000px] opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <div v-if="expanded" class="border-t border-gray-200">
        <div class="px-6 py-4 space-y-6">
          <!-- Summary -->
          <div class="sensitive bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-700 leading-relaxed">
              {{ subthread.thread_engagement_summary }}
            </p>
          </div>

          <!-- Performance Blockers Section -->
          <div v-if="performanceblockers.length > 0">
            <h5 class="text-sm font-semibold text-gray-900 mb-4 flex items-center">
              <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2 text-amber-600" />
              Performance Blockers
            </h5>

            <div class="space-y-3">
              <div
                v-for="blocker in performanceblockers"
                :key="blocker.object_id"
                class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-all duration-200"
              >
                <div class="grid grid-cols-12 gap-2 items-start">
                  <!-- Categories -->
                  <div class="col-span-2 space-y-2 py-3">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-primary-100 text-primary-800">
                      {{ blocker.performance_blocker_major_category }}
                    </span>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                      {{ blocker.performance_blocker_subcategory }}
                    </span>
                  </div>

                  <!-- Description -->
                  <div class="col-span-3">
                    <p class="text-xs text-gray-700 p-3">
                      {{ blocker.description }}
                    </p>
                  </div>

                  <!-- Evidence -->
                  <div class="col-span-4">
                    <div class="bg-gray-50 rounded-lg p-3 mr-2">
                      <p class="text-xs text-gray-600 italic">
                        {{ blocker.metadata?.evidence }}
                      </p>
                    </div>
                  </div>

                  <!-- Severity -->
                  <div class="col-span-1 w-full h-full p-2">
                    <div class="flex justify-center">
                      <div
                        class="px-3 py-1.5 rounded-md text-xs font-semibold text-white shadow-sm"
                        :style="{ backgroundColor: getSignificanceColor(blocker.metadata?.severity_estimate) }"
                      >
                        {{ blocker.metadata?.severity_estimate }}
                      </div>
                    </div>
                  </div>

                  <!-- Actions -->
                  <div class="col-span-2 flex justify-center">
                    <LLM_Suggestion_Feedback
                      :related-object-id="blocker.object_id"
                      :related-object-content-type-model="'performanceblocker'"
                      :show-thumbs-down="true"
                      :show-thumbs-up="true"
                      :show-delete="true"
                      :show-is-duplicate="true"
                      @delete="pb_list++"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading state -->
          <LoadingIndicator v-else-if="loading" message="Loading performance data..." />

          <!-- Empty state -->
          <div v-else class="py-8 text-center">
            <font-awesome-icon :icon="['fas', 'check-circle']" class="text-3xl text-green-500 mb-2" />
            <p class="text-sm text-gray-600">No performance blockers identified</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import LLM_Suggestion_Feedback from "@/components/widgetcomponents/LLM_Suggestion_Feedback.vue";
import LoadingIndicator from "@/components/widgetcomponents/library/LoadingIndicator.vue";

// Font Awesome imports
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faClock,
  faChartPie,
  faChevronDown,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons'

// Add icons to library
library.add(
  faClock,
  faChartPie,
  faChevronDown,
  faExclamationTriangle,
  faCheckCircle
)

export default {
  components: { FontAwesomeIcon, LLM_Suggestion_Feedback, LoadingIndicator },
  props: {
    subthread: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      actions: [],
      performanceblockers: [],
      expanded: false,
      action_list: 0,
      pb_list: 0,
      loading: false
    }
  },
  methods: {
    getSignificanceColor(level) {
      const colors = {
        "Not Useful": '#10B981', // Modern emerald
        "Marginal": '#F59E0B', // Modern amber
        "Somewhat Important": '#F97316', // Modern orange
        "Very Significant": '#EF4444' // Modern red
      };
      return colors[level] || '#6B7280'; // Default gray
    },

    toggleExpanded() {
      this.expanded = !this.expanded;

      // Only fetch data when expanded for the first time
      if (this.expanded && this.actions.length === 0 && this.performanceblockers.length === 0) {
        this.loading = true;
        this.fetchActions();
        this.fetchPerformanceBlockers();
      }
    },

    async fetchActions() {
      const request_data = {
        originating_object_id: this.subthread.id,
        originating_object_content_type_model: 'subthread'
      };

      try {
        const response = await axios.get(
          '/api/actions/',
          {
            params: request_data
          },
        );

        if (response.data?.length > 0) {
          this.actions = response.data;
        } else {
          this.actions = [];
        }
      } catch (error) {
        console.error('Error fetching actions:', error);
      }
    },

    async fetchPerformanceBlockers() {
      const request_data = {
        originating_object_id: this.subthread.id,
        originating_object_content_type_model: 'subthread'
      };

      try {
        const response = await axios.get(
          'api/performanceblockers/',
          {
            params: request_data
          }
        );

        if (response.data?.length > 0) {
          this.performanceblockers = response.data;
        } else {
          this.performanceblockers = [];
        }
        this.loading = false;
      } catch (error) {
        console.error('Error fetching performance blockers:', error);
        this.loading = false;
      }
    },
  },
  watch: {
    action_list() {
      this.fetchActions();
    },
    pb_list() {
      this.fetchPerformanceBlockers();
    }
  }
}
</script>

<style scoped>
</style>
